import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Eye, EyeOff, Play, AlertTriangle, Mail, Smartphone, Bell } from "lucide-react";
import EntityFormDialog from "@/components/EntityFormDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const TRIGGER_LABELS = {
  task_camere_non_eseguita: "Task camere non eseguita",
  task_scaduta: "Task scaduta",
  controlli_saltati_consecutivi: "Controlli saltati consecutivi",
  performance_sotto_soglia: "Performance sotto soglia",
  relazione_mensile_mancante: "Relazione mensile mancante",
  produttivita_hk_critica: "Produttività HK critica (anomaly)",
  audit_critico: "Audit critico",
  audit_in_ritardo: "Audit in ritardo",
};

const RUOLI_ALL = ["super_admin", "capo_area", "resident_manager", "housekeeping_manager", "front_office_manager", "read_only"];

const PRIO_COLORS = {
  bassa: "bg-slate-100 text-slate-700",
  media: "bg-blue-100 text-blue-700",
  alta: "bg-amber-100 text-amber-700",
  critica: "bg-rose-100 text-rose-700",
};

export default function ConfigurazioneAlert() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [evaluating, setEvaluating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/alert-rules");
      setRules(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try {
      await api.patch(`/alert-rules/${r.id}`, { attiva: !r.attiva });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const res = await api.post("/alerts/evaluate-rules");
      toast.success(`Valutazione completata: ${res.data.created_total} nuovi alert`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEvaluating(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Eliminare la regola "${r.nome}"?`)) return;
    try {
      await api.delete(`/alert-rules/${r.id}`);
      toast.success("Regola eliminata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="config-alert-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" /> Configurazione</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-light">Regole <span className="font-medium">Alert</span></h1>
          <p className="text-sm text-muted-foreground mt-1">{rules.filter((r) => r.attiva).length} di {rules.length} attive</p>
        </div>
        <div className="flex gap-2">
          <button onClick={evaluate} disabled={evaluating} data-testid="evaluate-btn" className="px-4 py-2 rounded-full border border-border text-sm font-medium flex items-center gap-2 hover:bg-accent/40 disabled:opacity-50">
            <Play className="h-4 w-4" /> {evaluating ? "Valutazione…" : "Esegui valutazione"}
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-rule-btn" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2"><Plus className="h-4 w-4" /> Nuova regola</button>
        </div>
      </div>

      <div className="ih-glass rounded-2xl p-6">
        {loading ? "Caricamento…" : (
          <div className="space-y-2">
            {rules.map((r) => (
              <div key={r.id} data-testid={`rule-${r.id}`} className={`rounded-xl border border-border/60 p-4 ${!r.attiva ? "opacity-60" : ""}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{r.nome}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${PRIO_COLORS[r.priorita]}`}>{r.priorita}</span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.trigger_type}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{r.descrizione}</div>
                    <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-wider text-muted-foreground flex-wrap">
                      Destinatari: {r.destinatari_ruoli.map((ro) => <span key={ro} className="px-1.5 py-0.5 rounded bg-accent/40">{ro}</span>)}
                      {Object.keys(r.parametri || {}).length > 0 && <span className="ml-2">Parametri: {JSON.stringify(r.parametri)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="flex items-center gap-1 text-[10px] uppercase text-emerald-600 mr-2"><Bell className="h-3 w-3" /> In-app</span>
                    <span className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground/50 cursor-not-allowed" title="Disponibile dalla prossima release"><Mail className="h-3 w-3" /> Email</span>
                    <span className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground/50 cursor-not-allowed ml-1" title="Disponibile dalla prossima release"><Smartphone className="h-3 w-3" /> Push</span>
                    <button onClick={() => toggle(r)} data-testid={`toggle-${r.id}`} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center ml-2">{r.attiva ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                    <button onClick={() => setEditing(r)} className="h-8 w-8 rounded-lg hover:bg-accent flex items-center justify-center"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => del(r)} className="h-8 w-8 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/30 text-rose-500 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(showAdd || editing) && (
        <RuleFormModal open={showAdd || !!editing} initial={editing} onClose={() => { setEditing(null); setShowAdd(false); }} onSaved={() => { setEditing(null); setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function RuleFormModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    nome: "", descrizione: "", trigger_type: "task_scaduta",
    parametri: {}, destinatari_ruoli: ["capo_area"], canali: ["in_app"],
    attiva: true, priorita: "media",
  });
  const [paramJson, setParamJson] = useState(JSON.stringify(form.parametri, null, 2));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    let parsed = {};
    try { parsed = paramJson.trim() ? JSON.parse(paramJson) : {}; } catch { return toast.error("JSON parametri non valido"); }
    setSaving(true);
    try {
      const body = { ...form, parametri: parsed };
      if (initial) {
        await api.patch(`/alert-rules/${initial.id}`, body);
        toast.success("Regola aggiornata");
      } else {
        await api.post("/alert-rules", body);
        toast.success("Regola creata");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const toggleRuolo = (ro) => {
    setForm((f) => ({
      ...f,
      destinatari_ruoli: f.destinatari_ruoli.includes(ro) ? f.destinatari_ruoli.filter((x) => x !== ro) : [...f.destinatari_ruoli, ro],
    }));
  };

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={initial ? "Modifica regola" : "Nuova regola"}
      description="Configura il trigger, i parametri e i destinatari."
      size="md"
      scrollable
      onSubmit={submit}
      submitLabel={initial ? "Salva" : "Crea regola"}
      saving={saving}
      testId="rule-form-modal"
      submitTestId="rule-save"
    >
      <div className="space-y-2">
        <Label>Nome</Label>
        <Input data-testid="rule-nome" required placeholder="Es. Avviso task camere non eseguite" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label>Descrizione</Label>
        <Input placeholder="Breve descrizione (opzionale)" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Trigger</Label>
          <Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v })}>
            <SelectTrigger data-testid="rule-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TRIGGER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Priorità</Label>
          <Select value={form.priorita} onValueChange={(v) => setForm({ ...form, priorita: v })}>
            <SelectTrigger data-testid="rule-prio"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bassa">Bassa</SelectItem>
              <SelectItem value="media">Media</SelectItem>
              <SelectItem value="alta">Alta</SelectItem>
              <SelectItem value="critica">Critica</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Destinatari (ruoli)</Label>
        <div className="flex flex-wrap gap-2 p-2 rounded-lg border border-border/60 bg-background/40">
          {RUOLI_ALL.map((ro) => {
            const checked = form.destinatari_ruoli.includes(ro);
            return (
              <button
                type="button"
                key={ro}
                onClick={() => toggleRuolo(ro)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${checked ? "bg-lavender-200 text-lavender-900" : "bg-background/60 text-muted-foreground hover:bg-accent/60"}`}
              >
                {ro}
              </button>
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Parametri (JSON)</Label>
        <Textarea data-testid="rule-params" value={paramJson} onChange={(e) => setParamJson(e.target.value)} rows={3} className="font-mono text-xs" placeholder='{"giorni":7,"soglia":60}' />
      </div>
      <label className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/40 px-3 py-2">
        <span className="text-sm">Regola attiva</span>
        <Switch checked={!!form.attiva} onCheckedChange={(v) => setForm({ ...form, attiva: v })} />
      </label>
    </EntityFormDialog>
  );
}
